import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TopPanelComponent from "./TopPanelComponent";
import { useDispatch } from "react-redux";
import { setDialog, setLoading } from "slices/appSlice";
import Api from "api";
import {
  Tabs,
  Tab,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useTheme,
} from "@mui/material";
import { postUserActivity } from "components/shared/postUserActivity";
import { WORKFLOW_CODING } from "constants/Constants";
import PoetIcon from "components/PoetIcon/PoetIcon";
import customizedTheme from "styles/theme";
import DiagnosisGroupTable from "./Tabs/DiagnosisGroupTable";
import ProcedureGroupTable from "./Tabs/ProcedureGroupTable";
import RemoveCodeGroupDialog from "./Dialogs/RemoveCodeGroupDialog";
import ValidationErrorsDialog from "components/shared/Dialogs/ValidationErrorsDialog";
import {
  getCodeGroupIdentity,
  getUnapprovedCodeGroups,
  unapprovedCodeGroupMessage,
} from "./Utils/codeGroupUtils";

function getPolicyState(state) {
  if (state == null || Object.keys(state).length === 0) {
    state = JSON.parse(sessionStorage.getItem("policyState"));
  } else {
    sessionStorage.setItem("codingState", JSON.stringify(state));
  }

  return state;
}

const rowValidation = (rule) => {
  if (!rule.editRule || rule.editRule === "EDIT") {
    return rule;
  }

  const errors = {};
  // If dxCodes or dxCodesExpanded is empty, show error
  if (
    rule.codingClassification === "paNotReqd" &&
    (!rule.dxCodes ||
      !rule.dxCodes.dxCodesExpanded ||
      typeof rule.dxCodes.dxCodesExpanded !== "string" ||
      rule.dxCodes.dxCodesExpanded.trim() === "")
  ) {
    errors.dxCodes = "DX Code(s) Required";
  }

  if (!rule.codingClassification) {
    errors.classification = "Classification Required";
  }
  if (!rule.ccNumber) {
    errors.cc = "CC# Required";
  }
  // Return a new object with errors
  return { ...rule, errors };
};

const PROCEDURE_CODE_GROUP_TYPE = "P";
const DIAGNOSIS_CODE_GROUP_TYPE = "D";

const REQUIRED_CODING_STATUSES = [
  "assigned",
  "in progress",
  "peer review",
  "pdc review",
];

/**
 * Returns true when a value is null, undefined, or whitespace-only.
 *
 * @param {string | null | undefined} value
 * @returns {boolean}
 */
const isBlank = (value) => String(value ?? "").trim() === "";

/**
 * Determines whether C&T review validation rules apply for the current status.
 *
 * @param {Object} policyState
 * @returns {boolean}
 */
const isReviewStatus = (policyState) => {
  const status = policyState?.policyWorkflowTaskStatus?.toLowerCase?.() || "";
  return status === "peer review" || status === "pdc review";
};

const getRuleTab = (ruleUnit) =>
  ruleUnit?.ruleUnitType?.ruleTab ||
  ruleUnit?.ruleUnitType?.ruleType ||
  ruleUnit?.ruleUnitType?.sheetName ||
  "";

const getRuleUnitVersions = (ruleUnitResponse) =>
  Object.values(ruleUnitResponse || {}).flat();

const mergeCodeGroupsWithRuleLinks = (
  policyCodeGroups,
  ruleUnits,
  codeType
) => {
  const linkMap = new Map();

  ruleUnits.forEach((ruleUnit) => {
    const groups =
      codeType === DIAGNOSIS_CODE_GROUP_TYPE
        ? ruleUnit.diagnosisCodeGroups || []
        : ruleUnit.procedureCodeGroups || [];

    groups.forEach((codeGroup) => {
      const identity = getCodeGroupIdentity(codeGroup);
      if (!identity) {
        return;
      }

      const current = linkMap.get(identity) || {
        codeGroup,
        hasImmutableRule: false,
        linkedRuleUnits: [],
      };
      current.hasImmutableRule =
        current.hasImmutableRule ||
        Boolean(codeGroup.hasImmutableRule) ||
        Boolean(ruleUnit.immutable);
      current.linkedRuleUnits.push({
        ruleUnitKey: ruleUnit.ruleUnitKey,
        ruleUnitName: ruleUnit.ruleUnitName,
        ruleTab: getRuleTab(ruleUnit),
        immutable: Boolean(ruleUnit.immutable),
      });
      linkMap.set(identity, current);
    });
  });

  const mergedCodeGroups = policyCodeGroups.map((codeGroup) => {
    const identity = getCodeGroupIdentity(codeGroup);
    const ruleLink = linkMap.get(identity);

    if (!ruleLink) {
      return { ...codeGroup, linkedRuleUnits: [] };
    }

    return {
      ...ruleLink.codeGroup,
      ...codeGroup,
      hasImmutableRule:
        Boolean(codeGroup.hasImmutableRule) || ruleLink.hasImmutableRule,
      linkedRuleUnits: ruleLink.linkedRuleUnits,
    };
  });

  const policyGroupIdentities = new Set(
    policyCodeGroups.map(getCodeGroupIdentity).filter(Boolean)
  );
  const linkedOnlyGroups = [...linkMap.entries()]
    .filter(([identity]) => !policyGroupIdentities.has(identity))
    .map(([, ruleLink]) => ({
      ...ruleLink.codeGroup,
      hasImmutableRule: ruleLink.hasImmutableRule,
      linkedRuleUnits: ruleLink.linkedRuleUnits,
    }));

  return [...mergedCodeGroups, ...linkedOnlyGroups];
};

const removeCodeGroup = (codeGroups, codeGroupToRemove) => {
  const removeIdentity = getCodeGroupIdentity(codeGroupToRemove);
  return codeGroups.filter(
    (codeGroup) => getCodeGroupIdentity(codeGroup) !== removeIdentity
  );
};

const filterRemovedCodeGroups = (codeGroups = [], removedCodeGroups) =>
  codeGroups.filter(
    (codeGroup) =>
      !removedCodeGroups.some(
        (removedCodeGroup) =>
          getCodeGroupIdentity(removedCodeGroup) ===
          getCodeGroupIdentity(codeGroup)
      )
  );

const buildRuleUnitUpdatePayload = (ruleUnits, removedCodeGroups) =>
  ruleUnits
    .map((ruleUnit) => {
      const procedureCodeGroups = filterRemovedCodeGroups(
        ruleUnit.procedureCodeGroups,
        removedCodeGroups
      );
      const diagnosisCodeGroups = filterRemovedCodeGroups(
        ruleUnit.diagnosisCodeGroups,
        removedCodeGroups
      );
      const procedureChanged =
        procedureCodeGroups.length !==
        (ruleUnit.procedureCodeGroups || []).length;
      const diagnosisChanged =
        diagnosisCodeGroups.length !==
        (ruleUnit.diagnosisCodeGroups || []).length;

      if (!procedureChanged && !diagnosisChanged) {
        return null;
      }

      return {
        ...ruleUnit,
        procedureCodeGroups,
        diagnosisCodeGroups,
        editRuleUnit: "EDIT",
      };
    })
    .filter(Boolean);

/**
 * Policy coding Post OHR page.
 *
 * @returns {JSX.Element}
 */
const PolicyCodingPostOHR = () => {
  const theme = useTheme();
  const warningIconColor =
    theme.palette.warningIcon?.main || customizedTheme.palette.warningIcon.main;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [activityUpdates, setActivityUpdates] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  const policyState = getPolicyState(location?.state?.policyState || {});
  const { policyNumber } = location.state;
  const { policyTitle } = location.state;
  const { policyVersion } = location.state;
  const editable = policyState.editMode || false;
  const [showCodingReviewerError, setShowCodingReviewerError] = useState(false);
  const [missingCoverageGroupIdentities, setMissingCoverageGroupIdentities] =
    useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);

  const healthPlanDisplayName =
    policyState?.healthPlanDisplayName || "[Health Plan]";

  const [policyHeader, setPolicyHeader] = useState({});
  const [diagnosisCodeGroups, setDiagnosisCodeGroups] = useState(
    policyState.codeGroups?.filter((c) => c.codeGroupType === "D") || []
  );

  const [procedureCodeGroups, setProcedureCodeGroups] = useState(
    policyState.codeGroups?.filter((c) => c.codeGroupType === "P") || []
  );

  const [ruleUnits, setRuleUnits] = useState([]);
  const [removedCodeGroups, setRemovedCodeGroups] = useState([]);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const topPanelRef = useRef(null);
  const filtersRef = useRef(null);
  const [policyTabsHeight, setPolicyTabsHeight] = useState("auto");
  const [versionWarningDialogOpen, setVersionWarningDialogOpen] =
    useState(false);
  const pendingSubmitArgsRef = useRef(null);

  const handleAccordionToggle = () => {
    setTimeout(() => {
      calculatePolicyTabsHeight();
    }, 400);
  };

  const calculatePolicyTabsHeight = () => {
    if (!filtersRef.current) {
      setTimeout(calculatePolicyTabsHeight, 200);
      return;
    }
    const topPanelHeight = topPanelRef.current
      ? topPanelRef.current.offsetHeight
      : 0;
    const filtersHeight = filtersRef.current
      ? filtersRef.current.offsetHeight
      : 0;
    const viewportHeight = window.innerHeight;

    setPolicyTabsHeight(viewportHeight - topPanelHeight - filtersHeight);
  };

  useLayoutEffect(() => {
    calculatePolicyTabsHeight(); // Initial calculation
    window.addEventListener("resize", calculatePolicyTabsHeight);
    return () => {
      window.removeEventListener("resize", calculatePolicyTabsHeight);
    };
  }, []);

  useEffect(() => {
    const loadRuleUnits = async () => {
      if (!policyState?.healthPlanKey || !policyState?.policyKey) {
        return;
      }

      dispatch(setLoading(true));
      try {
        const response = await Api.policyData.getPolicyRuleUnits(
          policyState.healthPlanKey,
          policyState.policyKey,
          "pdm"
        );
        const loadedRuleUnits = getRuleUnitVersions(response.data);
        setRuleUnits(loadedRuleUnits);
        setProcedureCodeGroups((currentGroups) =>
          mergeCodeGroupsWithRuleLinks(
            currentGroups,
            loadedRuleUnits,
            PROCEDURE_CODE_GROUP_TYPE
          )
        );
        setDiagnosisCodeGroups((currentGroups) =>
          mergeCodeGroupsWithRuleLinks(
            currentGroups,
            loadedRuleUnits,
            DIAGNOSIS_CODE_GROUP_TYPE
          )
        );
      } catch (e) {
        dispatch(setDialog({ message: e.message, title: "Error" }));
      } finally {
        dispatch(setLoading(false));
      }
    };

    loadRuleUnits();
  }, [dispatch, policyState?.healthPlanKey, policyState?.policyKey]);

  const handleCancelAction = () => {
    navigate(-1);
  };

  /**
   * Runs submit-time validation rules and surfaces aggregated errors.
   *
   * @param {Object} policyState
   * @param {Object | null} ctReviewData
   * @param {boolean} submit
   * @param {Object | null} updateAssignessReviewerDto
   * @returns {boolean}
   */
  const handleValidate = (
    policyState,
    ctReviewData,
    submit,
    updateAssignessReviewerDto
  ) => {
    if (!submit) {
      return true;
    }

    const currentStatus =
      policyState?.policyWorkflowTaskStatus?.toLowerCase?.() || "";
    if (!REQUIRED_CODING_STATUSES.includes(currentStatus)) {
      return true;
    }

    const collectedErrors = [];
    const nonInlineErrors = [];
    const missingCoverageErrors = [];

    if (isBlank(updateAssignessReviewerDto?.codingReviewer)) {
      setShowCodingReviewerError(true);
      collectedErrors.push("Coding Reviewer Required.");
    }

    const allCodeGroups = [...procedureCodeGroups, ...diagnosisCodeGroups];
    allCodeGroups.forEach((codeGroup) => {
      if (isBlank(codeGroup?.coverageCriteriaList)) {
        missingCoverageErrors.push(getCodeGroupIdentity(codeGroup));
      }
    });

    if (missingCoverageErrors.length > 0) {
      collectedErrors.push("CC required.");
    }

    if (procedureCodeGroups.length === 0) {
      const errorMessage = "Procedure Code Group required.";
      collectedErrors.push(errorMessage);
      nonInlineErrors.push(errorMessage);
    }

    const allProceduresArePaOnly =
      procedureCodeGroups.length > 0 &&
      procedureCodeGroups.every((group) => Boolean(group.isPaOnly));
    if (diagnosisCodeGroups.length === 0 && !allProceduresArePaOnly) {
      const errorMessage = "Diagnosis Code Group required.";
      collectedErrors.push(errorMessage);
      nonInlineErrors.push(errorMessage);
    }

    const unapprovedCodeGroups = getUnapprovedCodeGroups(
      procedureCodeGroups,
      diagnosisCodeGroups
    );
    unapprovedCodeGroups.forEach(({ codeGroupName }) => {
      const errorMessage = unapprovedCodeGroupMessage(codeGroupName);
      collectedErrors.push(errorMessage);
      nonInlineErrors.push(errorMessage);
    });

    if (
      policyState.policyWorkflowTaskCategory?.toLowerCase() === "coding" &&
      isReviewStatus(policyState)
    ) {
      if (!ctReviewData || !ctReviewData.reviewType || !ctReviewData.results) {
        const errorMessage = "C&T Review is required.";
        collectedErrors.push(errorMessage);
        nonInlineErrors.push(errorMessage);
      } else {
        const results = String(ctReviewData.results || "")
          .trim()
          .toLowerCase();
        const comments = String(ctReviewData.comments || "").trim();
        if (results === "fail" && isBlank(comments)) {
          const errorMessage =
            "C&T Review comments are required when Results is Fail.";
          collectedErrors.push(errorMessage);
          nonInlineErrors.push(errorMessage);
        }
      }
    }

    setMissingCoverageGroupIdentities(missingCoverageErrors);

    if (collectedErrors.length > 0) {
      if (nonInlineErrors.length > 0) {
        setValidationErrors(nonInlineErrors);
        setValidationDialogOpen(true);
      } else {
        setValidationErrors([]);
        setValidationDialogOpen(false);
      }
      return false;
    }

    setValidationErrors([]);
    setValidationDialogOpen(false);
    return true;
  };

  /**
   * Persists coding policy updates and drives submit intent via isSubmit.
   */
  const updatePolicyData = async (
    policyState,
    submit,
    policyHistoryData,
    ctReviewData,
    updateAssignessReviewerDto,
    policyChangeControlXrefDto
  ) => {
    const payload = {
      policyKey: policyState?.policyKey,
      policyStage: "Coding",
      policyCoder: updateAssignessReviewerDto?.coding || null,
      codingReviewer: updateAssignessReviewerDto?.codingReviewer || null,
      policyChangeControlXrefDto,
      policyHistoryData,
      diagnosisCodeGroups,
      procedureCodeGroups,
    };

    // Conditionally include ctReviewData only when non-null
    // On first submit (Assigned/In Progress), ctReviewData is null so it's omitted
    // On subsequent submits after page reload, ctReviewData is included
    if (ctReviewData != null) {
      payload.ctReviewData = ctReviewData;
    }

    dispatch(setLoading(true));

    try {
      const response = await Api.policyData.updatePolicyData(
        policyState?.healthPlanKey,
        policyState?.policyKey,
        payload,
        submit
      );

      if (response.status === 200) {
        const ruleUnitPayload = buildRuleUnitUpdatePayload(
          ruleUnits,
          removedCodeGroups
        );
        if (ruleUnitPayload.length > 0) {
          await Api.policyData.updatePolicyRuleUnits(
            policyState?.healthPlanKey,
            policyState?.policyKey,
            "pdm",
            ruleUnitPayload
          );
        }

        if (response.data?.diagnosisCodeGroups) {
          setDiagnosisCodeGroups(response.data.diagnosisCodeGroups);
        }

        if (response.data?.procedureCodeGroups) {
          setProcedureCodeGroups(response.data.procedureCodeGroups);
        }

        try {
          const update = submit ? "Submit" : "Update";
          await postUserActivity(
            policyState?.healthPlanKey,
            policyState?.policyKey,
            response.status,
            WORKFLOW_CODING,
            "click",
            "updatePolicyData",
            update,
            "Policy Coding",
            activityUpdates
          );
        } catch (error) {
          console.log("Failed to post user activity:", error);
          dispatch(
            setDialog({
              title: "Error",
              message: error.message || "Failed to post user activity",
            })
          );
        }
        navigate(-1);
      }
    } catch (e) {
      // Check if API returned validation errors
      const apiErrors =
        e.response?.data?.errors || e.response?.data?.validationErrors || [];

      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        // Show API validation errors in the dialog
        setValidationErrors(apiErrors);
        setValidationDialogOpen(true);
      } else {
        // Show generic error dialog for other errors
        dispatch(setDialog({ message: e.message, title: "Error" }));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  const removeCodeGroupFromPolicy = (codeGroup, codeGroupType) => {
    if (!codeGroup || codeGroup.hasImmutableRule) {
      return;
    }

    const removeFromState =
      codeGroupType === DIAGNOSIS_CODE_GROUP_TYPE
        ? setDiagnosisCodeGroups
        : setProcedureCodeGroups;

    removeFromState((currentGroups) =>
      removeCodeGroup(currentGroups, codeGroup)
    );
    setRemovedCodeGroups((currentGroups) => {
      if (
        currentGroups.some(
          (group) =>
            getCodeGroupIdentity(group) === getCodeGroupIdentity(codeGroup)
        )
      ) {
        return currentGroups;
      }
      return [...currentGroups, codeGroup];
    });
  };

  const handleRemoveCodeGroup = (codeGroup, codeGroupType) => {
    if (!codeGroup || codeGroup.hasImmutableRule) {
      return;
    }

    if (codeGroup.linkedRuleUnits?.length) {
      setPendingRemoval({ codeGroup, codeGroupType });
      return;
    }

    removeCodeGroupFromPolicy(codeGroup, codeGroupType);
  };

  const handleConfirmRemoveCodeGroup = () => {
    if (pendingRemoval) {
      removeCodeGroupFromPolicy(
        pendingRemoval.codeGroup,
        pendingRemoval.codeGroupType
      );
    }
    setPendingRemoval(null);
  };

  /**
   * Checks if any code group (diagnosis or procedure) has an older version.
   *
   * @returns {boolean} True if any code group has isLatestVersion === false
   */
  const hasStaleCodeGroups = () => {
    return (
      diagnosisCodeGroups.some((g) => g.isLatestVersion === false) ||
      procedureCodeGroups.some((g) => g.isLatestVersion === false)
    );
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div ref={topPanelRef}>
        <TopPanelComponent
          policyStateData={policyState}
          policyHeader={policyHeader}
          policyKey={policyState?.policyKey}
          healthPlanDisplayName={healthPlanDisplayName}
          healthPlanKey={policyState?.healthPlanKey}
          updatePolicyData={(
            submit,
            policyHistoryData,
            ctReviewData,
            updateAssignessReviewerDto,
            policyChangeControlXrefDto
          ) => {
            // If validation fails, short-circuit (return immediately).
            if (
              !handleValidate(
                policyState,
                ctReviewData,
                submit,
                updateAssignessReviewerDto
              )
            ) {
              return;
            }
            // Check for stale code groups on submit
            if (submit && hasStaleCodeGroups()) {
              pendingSubmitArgsRef.current = {
                policyState,
                submit,
                policyHistoryData,
                ctReviewData,
                updateAssignessReviewerDto,
                policyChangeControlXrefDto,
              };
              setVersionWarningDialogOpen(true);
              return;
            }
            updatePolicyData(
              policyState,
              submit,
              policyHistoryData,
              ctReviewData,
              updateAssignessReviewerDto,
              policyChangeControlXrefDto
            );
          }}
          handleCancelAction={handleCancelAction}
          policyNumber={policyNumber}
          policyTitle={policyTitle}
          policyVersion={policyVersion}
          editable={editable}
          showCodingReviewerError={showCodingReviewerError}
          onCodingReviewerChange={(value) => {
            if (!isBlank(value)) {
              setShowCodingReviewerError(false);
            }
          }}
        />
      </div>
      <div style={{ overflow: "hidden", paddingTop: "5px" }}>
        <Box sx={{ mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Procedure Codes
                  {procedureCodeGroups.some(
                    (g) => g.isLatestVersion === false
                  ) && (
                    <Tooltip title="One or more procedure code groups have newer versions available">
                      <div>
                        <PoetIcon iconName="warning" color={warningIconColor} />
                      </div>
                    </Tooltip>
                  )}
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Diagnosis Codes
                  {diagnosisCodeGroups.some(
                    (g) => g.isLatestVersion === false
                  ) && (
                    <Tooltip title="One or more diagnosis code groups have newer versions available">
                      <div>
                        <PoetIcon iconName="warning" color={warningIconColor} />
                      </div>
                    </Tooltip>
                  )}
                </Box>
              }
            />
          </Tabs>
        </Box>
        {tabValue === 0 && (
          <ProcedureGroupTable
            healthPlanKey={policyState?.healthPlanKey}
            codeGroups={procedureCodeGroups}
            setCodeGroups={setProcedureCodeGroups}
            missingCoverageGroupIdentities={missingCoverageGroupIdentities}
            onRemoveCodeGroup={(codeGroup) =>
              handleRemoveCodeGroup(codeGroup, PROCEDURE_CODE_GROUP_TYPE)
            }
          />
        )}
        {tabValue === 1 && (
          <DiagnosisGroupTable
            healthPlanKey={policyState?.healthPlanKey}
            codeGroups={diagnosisCodeGroups}
            setCodeGroups={setDiagnosisCodeGroups}
            missingCoverageGroupIdentities={missingCoverageGroupIdentities}
            onRemoveCodeGroup={(codeGroup) =>
              handleRemoveCodeGroup(codeGroup, DIAGNOSIS_CODE_GROUP_TYPE)
            }
          />
        )}
      </div>
      <Dialog
        open={versionWarningDialogOpen}
        onClose={() => {
          setVersionWarningDialogOpen(false);
          pendingSubmitArgsRef.current = null;
        }}
      >
        <DialogTitle>Code Group Version Warning</DialogTitle>
        <DialogContent>
          <DialogContentText>
            One or more linked code groups have newer approved versions
            available. Do you want to proceed with submission?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setVersionWarningDialogOpen(false);
              if (pendingSubmitArgsRef.current) {
                const args = pendingSubmitArgsRef.current;
                pendingSubmitArgsRef.current = null;
                updatePolicyData(
                  args.policyState,
                  args.submit,
                  args.policyHistoryData,
                  args.ctReviewData,
                  args.updateAssignessReviewerDto,
                  args.policyChangeControlXrefDto
                );
              }
            }}
          >
            PROCEED
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setVersionWarningDialogOpen(false);
              pendingSubmitArgsRef.current = null;
            }}
          >
            CANCEL
          </Button>
        </DialogActions>
      </Dialog>
      <RemoveCodeGroupDialog
        open={Boolean(pendingRemoval)}
        linkedRuleUnits={pendingRemoval?.codeGroup?.linkedRuleUnits || []}
        onConfirm={handleConfirmRemoveCodeGroup}
        onClose={() => setPendingRemoval(null)}
      />
      <ValidationErrorsDialog
        open={validationDialogOpen}
        errors={validationErrors}
        onClose={() => setValidationDialogOpen(false)}
      />
    </div>
  );
};

export default PolicyCodingPostOHR;
